import { forwardRef } from 'react';
import type { ReactElement } from 'react';

// The `icons` subpath, not the package root: the MUI theme reaches this file through `icons/index.ts`,
// and `.storybook/ThemeDecorator.tsx` imports the theme. Through the root, the whole design system sat
// in Storybook preview's import graph, so Chromatic's TurboSnap re-snapshotted every story on any DS
// change (about 60% of `main` builds).
import {
  BlurIcon,
  CaretDownIcon,
  DotsThreeIcon,
  MinusRectangleIcon,
  NatalieMarkIcon,
  TextAlignLeftIcon,
  UserSoundIcon,
  WaveformIcon,
} from '@natter-co/design-system/icons';
// `dist/lib/*` rather than `./lib`: the package's `./lib` export points at a `dist/lib/index.es.js` it
// does not ship.
import IconBase from '@phosphor-icons/react/dist/lib/IconBase';

import type { Icon as PhosphorIcon, IconProps, IconWeight } from '@phosphor-icons/react';

/**
 * DS 2.0 glyphs wrapped as Phosphor icons, for `ICON_SWAPS` rows where DS 2.0 redrew the Phosphor
 * glyph. Built on Phosphor's own `IconBase`, so props, grid and ref forwarding match every other swap.
 *
 * The DS glyph is drawn on the same `0 0 256 256` grid, so it nests at full size and stays the single
 * source of its geometry. Every weight resolves to it, so a `swapTo(…, 'bold')` cannot render blank.
 *
 * The cast is only because `common/typings/react.d.ts` narrows `forwardRef`'s return type, dropping
 * the `ForwardRefExoticComponent` brand Phosphor's `Icon` type requires.
 */
const createGlyph = (glyph: ReactElement, displayName: string) => {
  const weights = new Map<IconWeight, ReactElement>(
    (['thin', 'light', 'regular', 'bold', 'fill', 'duotone'] as const).map((weight) => [weight, glyph])
  );
  const Glyph = forwardRef<SVGSVGElement, IconProps>((props, ref) => (
    <IconBase ref={ref} {...props} weights={weights} />
  )) as PhosphorIcon;
  Glyph.displayName = displayName;

  return Glyph;
};

const DOTS = <DotsThreeIcon size={256} />;

/** Phosphor's `dots-three` draws its dots at about half the size of the DS 2.0 `DotsThree`. */
export const NatterDotsThree = createGlyph(DOTS, 'NatterDotsThree');

/** DS 2.0 has no vertical overflow glyph, so ⋮ is the same dots turned upright. */
export const NatterDotsThreeVertical = createGlyph(
  <g transform="rotate(90 128 128)">{DOTS}</g>,
  'NatterDotsThreeVertical'
);

/** Natter-drawn "step off the stage": a rectangle with a minus. Phosphor has no equivalent. */
export const NatterMinusRectangle = createGlyph(<MinusRectangleIcon size={256} />, 'NatterMinusRectangle');

/** A person speaking: a Natalie conversation. */
export const NatterUserSound = createGlyph(<UserSoundIcon size={256} />, 'NatterUserSound');

/** Voice: the waveform, so the swap carries the DS 2.0 redraw. */
export const NatterWaveform = createGlyph(<WaveformIcon size={256} />, 'NatterWaveform');

/** The transcript button's text lines. */
export const NatterTextAlignLeft = createGlyph(<TextAlignLeftIcon size={256} />, 'NatterTextAlignLeft');

/** Background blur: a Natter-drawn halftone, dots shrinking towards the edge (DES-448). Phosphor has none. */
export const NatterBlur = createGlyph(<BlurIcon size={256} />, 'NatterBlur');

/** The Natalie bubble and sparkle, drawn as an outline at the DS stroke. Natter-drawn, no Phosphor source. */
export const NatterNatalieMark = createGlyph(<NatalieMarkIcon size={256} />, 'NatterNatalieMark');

/**
 * The DS caret at 16px inside whatever box the caller gives it (18px on a small MUI `endIcon`). Nesting the
 * glyph in a larger viewBox shrinks it, so the caret stays 16/18 of the box instead of filling it.
 */
export const NatterCaretDown16 = createGlyph(
  <g transform="translate(14.2 14.2) scale(0.8889)">
    <CaretDownIcon size={256} />
  </g>,
  'NatterCaretDown16'
);
